import React, { useEffect, useState } from 'react';
import { Table, Typography, Tag, message, Select, Space, Card } from 'antd';
import { useNavigate } from 'react-router-dom';
import campaignLinkRequestService from "../../../services/campaignLinkRequestService";
import { useIsMobile } from "../../../utils/helpers";
import { CampaignLinkRequestObject } from "../../../utils/types";

const { Text } = Typography;
const { Option } = Select;

const getCampaignTitle = (item: CampaignLinkRequestObject) =>
    typeof item.campaignId === 'object' ? item.campaignId?.title : undefined;

const getPromoterUsername = (item: CampaignLinkRequestObject) =>
    typeof item.promoterId === 'object' ? item.promoterId?.username : undefined;

const CampaignLinkRequestsPage: React.FC = () => {
    const [requests, setRequests] = useState<CampaignLinkRequestObject[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

    const navigate = useNavigate();
    const isMobile = useIsMobile(); // ✅ same pattern as campaign page

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await campaignLinkRequestService.getAllRequests({
                status: statusFilter as any
            });
            setRequests(res.data.requests || []);
        } catch (e: any) {
            message.error(e.message || 'Failed to load campaign requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [statusFilter]);

    const getStatusTag = (status: string) => {
        const color =
            status === 'pending' ? 'orange' :
                status === 'approved' ? 'green' :
                    'red';

        return <Tag color={color}>{status}</Tag>;
    };

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
            <label className="title">Campaign requests</label>

            {/* Filter */}
            <div style={{ margin: '16px 0' }}>
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Text>Status:</Text>
                    <Select
                        allowClear
                        style={{ width: isMobile ? '100%' : 180 }}
                        placeholder="All"
                        value={statusFilter}
                        onChange={(v) => setStatusFilter(v)}
                    >
                        <Option value="pending">Pending</Option>
                        <Option value="approved">Approved</Option>
                        <Option value="rejected">Rejected</Option>
                    </Select>
                </Space>
            </div>

            {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {requests.map((item) => (
                        <Card
                            key={item._id}
                            loading={loading}
                            onClick={() => navigate(`/campaign-requests/${item._id}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div style={{ marginBottom: 8 }}>
                                <Text strong>{getCampaignTitle(item)}</Text>
                            </div>

                            <div>
                                <Text type="secondary">Promoter:</Text>{" "}
                                {getPromoterUsername(item)}
                            </div>

                            <div>
                                <Text type="secondary">Followers:</Text>{" "}
                                {item?.numberOfFollowers}
                            </div>

                            <div style={{ marginTop: 6 }}>
                                {getStatusTag(item.status)}
                            </div>

                            <div style={{ marginTop: 6 }}>
                                <Text type="secondary">
                                    {new Date(item.createdAt).toLocaleString()}
                                </Text>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <Table
                    rowKey="_id"
                    loading={loading}
                    dataSource={requests}
                    onRow={(record) => ({
                        onClick: () => navigate(`/campaign-requests/${record._id}`),
                        style: { cursor: 'pointer' }
                    })}
                    columns={[
                        {
                            title: 'Campaign',
                            key: 'campaign',
                            render: (_: unknown, item: CampaignLinkRequestObject) => getCampaignTitle(item)
                        },
                        {
                            title: 'Promoter',
                            key: 'promoter',
                            render: (_: unknown, item: CampaignLinkRequestObject) => getPromoterUsername(item)
                        },
                        {
                            title: 'Followers',
                            dataIndex: 'numberOfFollowers',
                            key: 'numberOfFollowers'
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (status: string) => getStatusTag(status)
                        },
                        {
                            title: 'Requested at',
                            dataIndex: 'createdAt',
                            key: 'createdAt',
                            render: (value: string) =>
                                new Date(value).toLocaleString()
                        }
                    ]}
                />
            )}
        </div>
    );
};

export default CampaignLinkRequestsPage;
